import { BaseProjectIdProps } from "@custom-types/base-types";
import { useLazyImage } from "@hooks/use-lazy-image";
import { Box } from "@mui/material";
import { useAppParams } from "@router/router-helper";
import {
  THUMBNAIL_IMAGE_HEIGHT,
  THUMBNAIL_IMAGE_WIDTH,
} from "@styles/common-styles";
import { defaultProjectThumbnail } from "@utils/project-utils";

interface Props extends BaseProjectIdProps {
  /** The thumbnail URL of the project */
  thumbnailUrl: string | null;
}

/**
 * Thumbnail image for the projects table that uses the shared image cache.
 * This ensures images cached by the card view are reused, avoiding redundant network requests
 * when the MUI DataGrid virtualizes rows during scrolling.
 */
export function ProjectTableThumbnail({
  thumbnailUrl,
  projectId,
}: Props): JSX.Element {
  const { companyId } = useAppParams();
  const src = thumbnailUrl ?? defaultProjectThumbnail;

  const { imageUrl } = useLazyImage(src, `${companyId}`, "project", projectId);

  return (
    <Box
      component="img"
      src={imageUrl ?? src}
      sx={{
        height: `${THUMBNAIL_IMAGE_HEIGHT}px`,
        width: `${THUMBNAIL_IMAGE_WIDTH}px`,
        objectFit: "cover",
      }}
      alt="Project thumbnail"
    />
  );
}
